import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import {CAMERA_VIEW, CAMERA_VIEW_OPTIONS, isPlanarOrthoView} from "./cameraViews";

const viewBtnSx = (isActive) => ({
    px: 1.25,
    py: 0.5,
    minWidth: 0,
    borderRadius: "6px",
    border: isActive ? "2px solid" : "1px solid",
    borderColor: isActive ? "primary.main" : "primary.stroke",
    backgroundColor: isActive ? "primary.light" : "#fff",
    color: "black",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": {
        borderColor: "primary.main",
    },
    "&:disabled": {
        opacity: 0.55,
        cursor: "default",
    },
});

const actionBtnSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    minWidth: 0,
    borderRadius: "6px",
    border: "1px solid",
    borderColor: "primary.stroke",
    backgroundColor: "#fff",
    color: "black",
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": {
        borderColor: "primary.main",
        backgroundColor: "primary.light",
    },
    "&:disabled": {
        opacity: 0.45,
        cursor: "default",
        "&:hover": {
            borderColor: "primary.stroke",
            backgroundColor: "#fff",
        },
    },
};

/**
 * Плашка стандартных видов над сценой + вторая строка действий (назад/вперёд…).
 */
const Module3DViewBar = ({
    activeCameraView = null,
    onCameraViewChange,
    disabled = false,
    canUndo = false,
    canRedo = false,
    onUndo,
    onRedo,
    showHistoryActions = true,
    showViewHint = true,
    /** Отступ справа (px), чтобы не перекрывать absolute-элементы над сценой (2D/3D). */
    overlayReserveRightPx = 0,
}) => {
    const planarHint = isPlanarOrthoView(activeCameraView);
    const actionsDisabled = Boolean(disabled);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                px: 1.25,
                py: 1,
                ...(overlayReserveRightPx > 0
                    ? {pr: `${overlayReserveRightPx}px`}
                    : {}),
                borderBottom: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                backgroundColor: "#F5F6FA",
                flexShrink: 0,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: "black",
                        mr: 0.5,
                    }}
                >
                    Вид
                </Typography>
                {CAMERA_VIEW_OPTIONS.map(({id, label}) => (
                    <Box
                        key={id}
                        component="button"
                        type="button"
                        disabled={disabled}
                        onClick={() => onCameraViewChange?.(id)}
                        title={`Камера: ${label}`}
                        sx={viewBtnSx(activeCameraView === id)}
                    >
                        {label}
                    </Box>
                ))}
            </Box>

            {showHistoryActions ? (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: "black",
                        mr: 0.5,
                    }}
                >
                    Действия
                </Typography>
                <Box
                    component="button"
                    type="button"
                    disabled={actionsDisabled || !canUndo}
                    onClick={() => onUndo?.()}
                    title="Назад (отменить последнее изменение сцены)"
                    sx={actionBtnSx}
                >
                    <UndoIcon sx={{fontSize: 16}}/>
                    Назад
                </Box>
                <Box
                    component="button"
                    type="button"
                    disabled={actionsDisabled || !canRedo}
                    onClick={() => onRedo?.()}
                    title="Вперёд (вернуть отменённое изменение)"
                    sx={actionBtnSx}
                >
                    <RedoIcon sx={{fontSize: 16}}/>
                    Вперёд
                </Box>
            </Box>
            ) : null}

            {showViewHint ? (
            <Typography
                sx={{
                    fontSize: "0.7rem",
                    lineHeight: 1.35,
                    color: "text.secondary",
                }}
            >
                {planarHint
                    ? "Плоский вид: можно приближать и двигать, вращение отключено."
                    : activeCameraView === CAMERA_VIEW.PERSPECTIVE
                        ? "Перспектива: вращение, панорама и зум мышью."
                        : "Аксонометрия: объём без перспективы; вращение, панорама и зум."}
            </Typography>
            ) : null}
        </Box>
    );
};

export default Module3DViewBar;
